import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AuthService } from '../singleton-services/auth/auth.service'
import { ActivatedRoute, Router } from '@angular/router'
import { VideoService } from '../shared/services/video.service'
import { NodeService } from '../shared/services/node.service'
import { VerticalService } from '../singleton-services/vertical.service'
import { catch301Errors } from '../shared/models/api-response.model'
import { AdComponentInputs } from '../shared/ads/ad/ad-component.interface'
import { AdConfig, AdTargeting, EqAdTargeting } from '../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_INTERSTITIAL_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_RECTANGLE_SMALL_SIZE,
  AD_SKIN_SIZE
} from '../shared/ads/ads.utility'
import { ContentItem } from '../libs/primary-related-content/primary-related-content.component'
import { RelatedContentService } from '../libs/primary-related-content/related-content.service'
import { Video } from '../shared/models/video.model'
import { AdComponent } from '../shared/ads/ad/ad.component'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { UpdateContentTargetingService } from '../shared/ads/advertising-service/update-content-targeting.service'
import { buildAuthorDate } from '../shared/author-avatar/author-date/author-date.utility'
import { VideosAnalyticsService } from './videos-analytics.service'
import { buildContentTagString } from '../shared/analytics/services/segment.utility'
import { ResourceType } from '../singleton-services/auth/auth.interfaces'
import { getIdFromSlug } from '../shared/utility-functions/slug.utility'

type HasInstance<A> = { instance: A }

@Component({
  selector: 'flo-videos',
  templateUrl: './videos.component.html',
  styleUrls: ['./videos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideosComponent implements AfterViewInit, OnDestroy {
  constructor(
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly ar: ActivatedRoute,
    private readonly vs: VideoService,
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly nodeService: NodeService,
    private readonly videoAnalytics: VideosAnalyticsService,
    private readonly verticalService: VerticalService,
    private readonly relatedContent: RelatedContentService,
    private readonly updateAdTargeting: UpdateContentTargetingService
  ) {}

  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly ngAfterViewInit$ = new Subject<void>()
  private readonly loadedAds$ = new BehaviorSubject<AdComponent[]>([])
  private slug$ = this.ar.params.pipe(map(a => a.slug))
  private videoId$ = this.slug$.pipe(map(slug => getIdFromSlug(slug)))

  private getVideo(id: number) {
    return this.vs.getVideo(id).pipe(
      map(res => res.data),
      catch301Errors
    )
  }

  private getVideoById$ = this.videoId$.pipe(
    mergeMap(id => this.getVideo(id)),
    shareReplay(1)
  )

  private user$ = this.auth.userIdentity$.pipe(shareReplay(1))

  private hasAccess$ = combineLatest([this.videoId$, this.getVideoById$]).pipe(
    mergeMap(([videoId, video]) => {
      return this.auth.hasAccess({ premium: video.source_video.premium ?? false, id: videoId }, ResourceType.Node)
    })
  )

  public video$ = combineLatest([
    this.getVideoById$,
    this.verticalService.siteSettings$,
    this.hasAccess$,
    this.user$
  ]).pipe(
    map(([videoRail, settings, hasAccess, user]) => {
      const videoSet2 = videoRail.container2 ? [...videoRail.container2] : []
      const videoSet3 = videoRail.container3 ? [...videoRail.container3] : []
      const nextVideoId = (videoSet2[0] && videoSet2[0].id) || (videoSet3[0] && videoSet3[0].id) || undefined
      const mapper = (_video: any) => {
        const video = _video || {}

        return {
          ...video,
          imgUrl: video.asset && video.asset.url,
          nodeId: video.id,
          assocId:
            video.node &&
            video.node.primary_event_or_series_association &&
            video.node.primary_event_or_series_association.id,
          adsetId: settings.ooyala_master_ad_set || '',
          playerId: settings.ooyala_player_id || '',
          playerColor: settings.primary_color || '#fff',
          contentId: video.content_id,
          title: video.title,
          routerLink: ['../', `${video.id}-${video.slug}`]
        }
      }

      return {
        premium: videoRail.source_video.premium,
        live_event: videoRail.source_video.live_event,
        id: videoRail.source_video.id,
        node: videoRail.source_video.node,
        type: videoRail.source_video.type,
        // author: videoRail.source_video.author,
        // tslint:disable-next-line:max-line-length
        author: buildAuthorDate(
          videoRail.source_video.author,
          videoRail.source_video.publish_start_date as unknown as string
        ),
        isAuthorized: hasAccess, // user && user.isPremium(),
        isLoggedIn: user,
        source: mapper(videoRail.source_video),
        primaryAssoc: !videoRail.source_video.drill_through_link
          ? undefined
          : {
              ...videoRail.source_video.drill_through_link,
              routerLink: [videoRail.source_video.drill_through_link.slug_uri]
            },
        description: videoRail.source_video.description || videoRail.source_video.description_plain,
        nextVideoId
      }
    }),
    tap(view => this.nodeService.applyNodeMetaToPage(view.source)),
    shareReplay(1)
  )

  private pageCall$ = this.video$
    .pipe(
      distinctUntilChanged((video1, video2) => video1.id === video2.id),
      tap(video => {
        this.videoAnalytics.pageVideoDetail({
          id: video.source.id,
          is_premium: !!video.premium,
          content_tags: buildContentTagString(video.source?.node?.tags),
          primary_association_id: video?.primaryAssoc?.id
        })
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  onPlayed(nextVideoId: number | undefined) {
    if (!!nextVideoId) {
      this.router.navigate(['video', nextVideoId])
    }
  }

  public readonly checkShouldShowPrimaryAssociationLink = (video: Video & { primaryAssoc: any }): boolean =>
    video.primaryAssoc && video.primaryAssoc.type !== 'series'

  public readonly rightRailRelatedContentTitle = 'Related Content'

  /**
   * TODO: update from IVideo to flosports30 Video
   * IVideo is deprecated and inaccurate. It has `drill_through_link?` as
   * possibly undefined, but it always returns from the API as `DrillThroughLink | null`
   *
   */
  public readonly rightRailRelatedContent$: Observable<ReadonlyArray<ContentItem>> = this.video$.pipe(
    // If we don't have a primary association, use the video id
    map(v =>
      v.source.drill_through_link === null || v.source.drill_through_link === undefined
        ? [v.source.id]
        : [v.source.drill_through_link.id, v.source.id]
    ),
    switchMap(([id, selfId]) => {
      /**
       * Right rail content turned off as part of FloCollege launch {@link https://flocasts.atlassian.net/browse/CXP-8121}
       * Refer to {@link https://flocasts.atlassian.net/browse/CXP-8122} to restore.
       */
      return of([])
    })
  )

  public ngAfterViewInit(): void {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  // Ads
  private readonly targeting$: Observable<AdTargeting> = this.video$.pipe(
    withLatestFrom(this.auth.isPremium$),
    map(([video, isPremium]) => ({
      contentIds: video.source.aggregated_node_ids,
      categorySlugs: video.source.aggregated_category_slugs,
      analyticsTracking: video,
      isPremium
    })),
    distinctUntilChanged(EqAdTargeting.equals),
    shareReplay(1)
  )

  private refreshAds$ = this.targeting$
    .pipe(
      withLatestFrom(this.loadedAds$),
      switchMap(args => {
        const [targeting, loadedAds] = args
        this.logger.trace('Video: updating ad targeting for next played')
        return this.updateAdTargeting.updateLoadedAdsTargeting(targeting, loadedAds).pipe(map(_ => args))
      }),
      switchMap(([targeting, loadedAds]) => {
        this.logger.trace('Video: ad refresh triggered for next played', {
          targeting
        })
        return this.updateAdTargeting.refreshLoadedAds(loadedAds)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public readonly loadedAdsPush = (loadedAd: HasInstance<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }

  public readonly AdComponent = import('../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly adLeaderboardSmall$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'videos-flex-small', targeting)
        }
      }
    })
  )

  public readonly adLeaderboardLarge$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/728x90_flex', AD_LEADERBOARD_LARGE_FLEX_SIZE, 'videos-flex-large', targeting)
        }
      }
    })
  )

  public readonly rightRailAdProps$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'videos-right-rail-rectangle', targeting)
        }
      }
    })
  )

  public readonly rightRailStickyAdProps$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/300x250_2', AD_RECTANGLE_LARGE_SIZE, 'videos-ad-second-rectangle', targeting),
          additionalOpts: { maxRefreshTime: 60000 }
        }
      }
    })
  )

  public readonly adhesionAdProps$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/adhesion', AD_ADHESION_SIZE, 'videos-adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      }
    })
  )

  public readonly adSkinProps$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/atf', AD_SKIN_SIZE, 'videos-ad-skin', targeting)
        }
      }
    })
  )

  public readonly adInterstitialProps$: Observable<AdComponentInputs> = this.targeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('video/post/interstitial', AD_INTERSTITIAL_SIZE, 'videos-ad-interstitial', targeting)
        }
      }
    })
  )
}
